import React, { useState, useEffect } from 'react';
import axiosClient from '../../services/axiosClient';
import Swal from 'sweetalert2';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const Roles = () => {
	const [roles, setRoles] = useState([]);
	const [permissions, setPermissions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [view, setView] = useState('list'); // 'list', 'add', 'edit'
	const [currentRole, setCurrentRole] = useState(null);
	const [formData, setFormData] = useState({
		name: '',
		display_name: '',
		description: '',
		permissions: []
	});

	useEffect(() => {
		fetchRoles();
		fetchPermissions();
	}, []);

	const fetchRoles = async () => {
		try {
			setLoading(true);
			const response = await axiosClient.get('/admin/roles', {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const apiData = response?.data;
			// La structure retourne result directement comme un tableau
			let list = apiData?.result || apiData?.data || [];
			
			if (!Array.isArray(list)) {
				list = [];
			}
			
			console.log('Roles loaded:', list); // Debug
			setRoles(list);
		} catch (err) {
			console.error('Error fetching roles:', err);
			Swal.fire({
				title: 'Erreur !',
				text: 'Impossible de charger les rôles',
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setLoading(false);
		}
	};

	const fetchPermissions = async () => {
		try {
			const response = await axiosClient.get('/admin/permissions', {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const apiData = response?.data;
			// La structure retourne result directement comme un tableau
			let list = apiData?.result || apiData?.data || [];
			
			if (!Array.isArray(list)) {
				list = [];
			}
			
			console.log('Permissions loaded:', list); // Debug
			setPermissions(list);
		} catch (err) {
			console.error('Error fetching permissions:', err);
			// Fallback permissions
			setPermissions([
				{ id: 1, name: 'permission-list' },
				{ id: 2, name: 'permission-create' },
				{ id: 3, name: 'permission-update' },
				{ id: 4, name: 'permission-delete' },
				{ id: 5, name: 'role-list' },
				{ id: 6, name: 'role-create' },
				{ id: 7, name: 'role-update' },
				{ id: 8, name: 'role-delete' },
				{ id: 9, name: 'dashboard-access' }
			]);
		}
	};

	const handleAddRole = async () => {
		try {
			// Préparer les données avec les noms de permissions
			const permissionNames = formData.permissions.map(permId => {
				const perm = permissions.find(p => p.id === permId);
				return perm ? perm.name : permId;
			});
			
			const payload = {
				name: formData.name,
				display_name: formData.display_name,
				description: formData.description,
				permissions: permissionNames
			};
			
			const response = await axiosClient.post('/admin/roles', payload, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 201) {
				await fetchRoles();
				setView('list');
				setFormData({ name: '', display_name: '', description: '', permissions: [] });
				
				Swal.fire({
					title: 'Succès !',
					text: 'Le rôle a été ajouté avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error adding role:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de l\'ajout du rôle';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleEditRole = async (role) => {
		try {
			// Charger les détails complets du rôle
			const response = await axiosClient.get(`/admin/roles/${role.id}`, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const roleData = response?.data?.result || response?.data;
			
			setCurrentRole(roleData);
			setFormData({
				name: roleData.name || '',
				display_name: roleData.display_name || '',
				description: roleData.description || '',
				permissions: roleData.permissions?.map(p => {
					// Trouver l'ID de la permission par son nom
					const perm = permissions.find(per => per.name === p.name || per.name === p);
					return perm ? perm.id : (p.id || p);
				}) || []
			});
			setView('edit');
		} catch (err) {
			console.error('Error loading role details:', err);
			Swal.fire({
				title: 'Erreur !',
				text: 'Impossible de charger les détails du rôle',
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleUpdateRole = async () => {
		try {
			// Préparer les données avec les noms de permissions
			const permissionNames = formData.permissions.map(permId => {
				const perm = permissions.find(p => p.id === permId);
				return perm ? perm.name : permId;
			});
			
			const payload = {
				name: formData.name,
				display_name: formData.display_name,
				description: formData.description,
				permissions: permissionNames
			};
			
			const response = await axiosClient.put(
				`/admin/roles/${currentRole.id}`, 
				payload,
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchRoles();
				setView('list');
				setCurrentRole(null);
				setFormData({ name: '', display_name: '', description: '', permissions: [] });
				
				Swal.fire({
					title: 'Succès !',
					text: 'Le rôle a été mis à jour avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error updating role:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la mise à jour du rôle';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleDeleteRole = async (roleId, roleName) => {
		const result = await Swal.fire({
			title: 'Supprimer le rôle',
			text: `Voulez-vous vraiment supprimer le rôle "${roleName}" ? Cette action est irréversible.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			const response = await axiosClient.delete(
				`/admin/roles/${roleId}`,
				{
					headers: { 
						'Accept': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200 || response.status === 204) {
				await fetchRoles();
				
				Swal.fire({
					title: 'Supprimé !',
					text: 'Le rôle a été supprimé avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error deleting role:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression du rôle';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const togglePermission = (permissionId) => {
		if (formData.permissions.includes(permissionId)) {
			setFormData({
				...formData,
				permissions: formData.permissions.filter(id => id !== permissionId)
			});
		} else {
			setFormData({
				...formData,
				permissions: [...formData.permissions, permissionId]
			});
		}
	};

	// LIST VIEW
	if (view === 'list') {
		return (
			<>
				<style>
					{`
						.roles-table {
							background: white;
							border-radius: 12px;
							overflow: hidden;
							box-shadow: 0 2px 8px rgba(0,0,0,0.08);
						}
						
						.roles-table thead {
							background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
							color: white;
						}
						
						.roles-table thead th {
							font-weight: 600;
							padding: 16px 20px;
							border: none;
							font-size: 13px;
							text-transform: uppercase;
							letter-spacing: 0.5px;
						}
						
						.roles-table tbody tr {
							border-bottom: 1px solid #f0f0f0;
							transition: all 0.2s ease;
						}
						
						.roles-table tbody tr:hover {
							background-color: #f0fdf4;
						}
						
						.roles-table tbody td {
							padding: 18px 20px;
							vertical-align: middle;
							font-size: 14px;
						}
						
						.btn-modern {
							border-radius: 8px;
							padding: 10px 20px;
							font-weight: 500;
							transition: all 0.2s;
						}
						
						.btn-add-role {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
						}
						
						.btn-add-role:hover {
							transform: translateY(-2px);
							box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
							color: white;
						}
						
						.btn-edit-role {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
							padding: 8px 20px;
							border-radius: 6px;
							font-size: 13px;
							font-weight: 500;
						}
						
						.btn-edit-role:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
							color: white;
						}
						
						.btn-delete-role {
							background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
							border: none;
							color: white;
							padding: 8px 20px;
							border-radius: 6px;
							font-size: 13px;
							font-weight: 500;
							margin-left: 8px;
						}
						
						.btn-delete-role:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
							color: white;
						}
					`}
				</style>
			
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
									<i className="fas fa-user-shield me-3" style={{ color: '#1e7e34' }}></i>
									Roles
								</h2>
								<p className="text-muted">Affichage des éléments 1 à {roles.length} sur {roles.length}</p>
							</div>
							<div className="d-flex gap-2">
								<button className="btn btn-outline-secondary btn-modern">
									Tableau de bord
								</button>
								<button className="btn btn-outline-secondary btn-modern">
									Roles
								</button>
								<button className="btn btn-success btn-modern">
									Liste
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12">
						<button 
							className="btn btn-add-role btn-modern"
							onClick={() => setView('add')}
						>
							<i className="fas fa-plus me-2"></i>Ajouter Role
						</button>
					</div>
				</div>

				<div className="row mb-3">
					<div className="col-md-6">
						<span className="text-muted" style={{ fontSize: '14px' }}>25 enregistrement par page</span>
					</div>
					<div className="col-md-6">
						<div className="d-flex justify-content-end">
							<div className="input-group" style={{ maxWidth: '300px' }}>
								<span className="input-group-text bg-white border-end-0">
									Recherche:
								</span>
								<input 
									type="text" 
									className="form-control border-start-0" 
									placeholder="" 
									style={{ borderRadius: '0 8px 8px 0' }}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="roles-table">
					{loading ? (
						<div className="text-center py-5">
							<div className="spinner-border text-success"></div>
						</div>
					) : (
						<table className="w-100">
							<thead>
								<tr>
									<th>Nom</th>
									<th style={{ textAlign: 'right' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{roles.length === 0 ? (
									<tr>
										<td colSpan="2" className="text-center py-4">
											<p className="text-muted mb-0">Aucun rôle trouvé</p>
										</td>
									</tr>
								) : (
									roles.map((role) => (
										<tr key={role.id}>
											<td style={{ fontWeight: '500', color: '#1f2937' }}>{role.name}</td>
											<td style={{ textAlign: 'right' }}>
												<button 
													className="btn btn-edit-role"
													onClick={() => handleEditRole(role)}
												>
													<i className="fas fa-edit me-1"></i>Modifier
												</button>
												<button 
													className="btn btn-delete-role"
													onClick={() => handleDeleteRole(role.id, role.name)}
												>
													<i className="fas fa-trash me-1"></i>Supprimer
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>

				<div className="row mt-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<p className="text-muted mb-0">Affichage des éléments 1 à {roles.length} sur {roles.length}.</p>
							<div className="d-flex gap-2">
								<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '6px' }}>
									Précédent
								</button>
								<button className="btn btn-sm btn-success" style={{ borderRadius: '6px' }}>
									1
								</button>
								<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '6px' }}>
									Suivant
								</button>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}

	// ADD VIEW
	if (view === 'add') {
		return (
			<>
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700' }}>Roles</h2>
								<p className="text-muted">Ajouter Role</p>
							</div>
							<div className="d-flex gap-2">
								<button className="btn btn-outline-primary" style={{ borderRadius: '8px' }}>
									Tableau de bord
								</button>
								<button className="btn btn-outline-primary" style={{ borderRadius: '8px' }}>
									Roles
								</button>
								<button className="btn btn-primary" style={{ borderRadius: '8px' }}>
									Ajouter
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12">
						<button 
							className="btn btn-primary"
							onClick={() => setView('list')}
							style={{ borderRadius: '8px' }}
						>
							<i className="fas fa-arrow-left me-2"></i>Retour à la liste roles
						</button>
					</div>
				</div>

				<div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
					<div className="card-body p-4">
						<h4 className="mb-4">Ajouter un nouvel élément Role</h4>
						
						<div className="mb-4">
							<label className="form-label fw-bold">Nom <span className="text-danger">*</span></label>
							<input
								type="text"
								className="form-control"
								value={formData.name}
								onChange={(e) => setFormData({ ...formData, name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: moderateur"
							/>
							<small className="text-muted">Identifiant unique du rôle (sans espaces)</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Nom d'affichage</label>
							<input
								type="text"
								className="form-control"
								value={formData.display_name}
								onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: Modérateur"
							/>
							<small className="text-muted">Nom lisible du rôle</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Description</label>
							<textarea
								className="form-control"
								value={formData.description}
								onChange={(e) => setFormData({ ...formData, description: e.target.value })}
								style={{ borderRadius: '8px' }}
								rows="3"
								placeholder="Ex: Rôle de modération des contenus"
							/>
							<small className="text-muted">Description du rôle et de ses responsabilités</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold" style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
								Permissions
							</label>
							{permissions.length === 0 ? (
								<p className="text-muted">Chargement des permissions...</p>
							) : (
								<div className="row g-3 mt-2">
									{permissions.map((permission) => (
										<div key={permission.id} className="col-md-4">
											<div className="form-check d-flex align-items-center">
												<input
													className="form-check-input"
													type="checkbox"
													id={`perm-${permission.id}`}
													checked={formData.permissions.includes(permission.id)}
													onChange={() => togglePermission(permission.id)}
													style={{ 
														width: '18px', 
														height: '18px', 
														borderRadius: '3px',
														cursor: 'pointer',
														accentColor: '#3b82f6'
													}}
												/>
												<label 
													className="form-check-label ms-2" 
													htmlFor={`perm-${permission.id}`}
													style={{ 
														fontSize: '14px', 
														color: '#374151',
														cursor: 'pointer',
														userSelect: 'none'
													}}
												>
													{permission.name}
												</label>
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						<div className="d-flex gap-2">
							<button 
								className="btn"
								onClick={handleAddRole}
								disabled={!formData.name}
								style={{ 
									borderRadius: '8px',
									background: 'linear-gradient(135deg, #1e7e34 0%, #28a745 100%)',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-save me-2"></i>Enregistrer Et Retour À La List
							</button>
							<button 
								className="btn"
								onClick={() => setView('list')}
								style={{ 
									borderRadius: '8px',
									background: '#1f2937',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-times me-2"></i>Annuler
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}

	// EDIT VIEW
	if (view === 'edit') {
		return (
			<>
				<style>
					{`
						.edit-card {
							background: #f8f9fa;
							border-radius: 12px;
							padding: 40px;
						}
						
						.edit-content {
							background: white;
							border-radius: 8px;
							padding: 32px;
							box-shadow: 0 1px 3px rgba(0,0,0,0.1);
						}
						
						.permission-checkbox {
							width: 18px;
							height: 18px;
							border-radius: 3px;
							cursor: pointer;
							accent-color: #3b82f6;
						}
						
						.permission-label {
							font-size: 14px;
							color: #374151;
							cursor: pointer;
							user-select: none;
							margin-left: 8px;
						}
						
						.btn-save-edit {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
							padding: 12px 28px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-save-edit:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
							color: white;
						}
						
						.btn-cancel-edit {
							background: #6c757d;
							border: none;
							color: white;
							padding: 12px 28px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-cancel-edit:hover {
							background: #5a6268;
							color: white;
						}
						
						.btn-back-edit {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
							padding: 10px 20px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-back-edit:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
							color: white;
						}
						
						.role-name-display {
							font-size: 15px;
							color: #1f2937;
							font-weight: 500;
							margin-bottom: 24px;
						}
						
						.permissions-title {
							font-size: 14px;
							color: #6b7280;
							font-weight: 600;
							margin-bottom: 16px;
							text-transform: uppercase;
							letter-spacing: 0.5px;
						}
					`}
				</style>
			
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
									<i className="fas fa-user-shield me-3" style={{ color: '#1e7e34' }}></i>
									Roles
								</h2>
								<p className="text-muted">Modifier Role</p>
							</div>
							<div className="d-flex gap-2">
								<button className="btn btn-outline-secondary btn-modern">
									Tableau de bord
								</button>
								<button className="btn btn-outline-secondary btn-modern">
									Roles
								</button>
								<button className="btn btn-success btn-modern">
									Modifier
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12">
						<button 
							className="btn btn-back-edit"
							onClick={() => setView('list')}
						>
							<i className="fas fa-arrow-left me-2"></i>Retour à la liste
						</button>
					</div>
				</div>

				<div className="edit-card">
					<div className="edit-content">
						<h4 className="mb-4" style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
							Modifier
						</h4>
						
						<div className="role-name-display">
							<strong>Nom:</strong> {currentRole?.name}
						</div>

						<div className="permissions-title">
							Permissions
						</div>
						
						<div className="row g-3">
							{permissions.map((permission) => (
								<div key={permission.id} className="col-md-4">
									<div className="form-check d-flex align-items-center">
										<input
											className="permission-checkbox form-check-input"
											type="checkbox"
											id={`edit-perm-${permission.id}`}
											checked={formData.permissions.includes(permission.id)}
											onChange={() => togglePermission(permission.id)}
										/>
										<label className="permission-label form-check-label" htmlFor={`edit-perm-${permission.id}`}>
											{permission.name}
										</label>
									</div>
								</div>
							))}
						</div>

						<div className="d-flex gap-2 mt-4">
							<button 
								className="btn btn-save-edit"
								onClick={handleUpdateRole}
							>
								<i className="fas fa-save me-2"></i>Enregistrer et Retour à la list
							</button>
							<button 
								className="btn btn-cancel-edit"
								onClick={() => setView('list')}
							>
								<i className="fas fa-times me-2"></i>Annuler
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}

	return null;
};

export default Roles;
